
import { computed, defineComponent, ref, watch, PropType } from "vue";
import {
  IApiRoleItem,
  IAuthorizationPermissionGroup,
} from "@/core/interfaces/ApiResponses";
import { permissionGroupTitle } from "@/core/helpers/authorization-utils";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";

export default defineComponent({
  name: "user-authorization-panel",

  components: {
    LabelCustom,
  },

  props: {
    canManage: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    roleOptions: {
      type: Array as PropType<IApiRoleItem[]>,
      default: () => [],
    },
    permissionGroups: {
      type: Array as PropType<IAuthorizationPermissionGroup[]>,
      default: () => [],
    },
    selectedRoles: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    selectedPermissions: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },

  emits: [
    "update:selectedRoles",
    "update:selectedPermissions",
    "roles-change",
    "permissions-change",
  ],

  setup(props, { emit }) {
    const expandedGroups = ref<string[]>([]);

    watch(
      () => props.permissionGroups,
      (groups) => {
        expandedGroups.value = groups.map((g) => g.group);
      },
      { immediate: true }
    );

    const totalPermissionCount = computed(() =>
      props.permissionGroups.reduce(
        (sum, group) => sum + group.permissions.length,
        0
      )
    );

    const groupTitle = (group: IAuthorizationPermissionGroup) =>
      permissionGroupTitle(group.group, group.permissions.length);

    const selectedRolesModel = computed({
      get: () => props.selectedRoles,
      set: (value: string[]) => emit("update:selectedRoles", value),
    });

    const selectedPermissionsModel = computed({
      get: () => props.selectedPermissions,
      set: (value: string[]) => emit("update:selectedPermissions", value),
    });

    const onRolesChange = (value: string[]) => {
      emit("roles-change", value);
    };

    const onPermissionsChange = (value: string[]) => {
      emit("permissions-change", value);
    };

    return {
      expandedGroups,
      groupTitle,
      totalPermissionCount,
      selectedRolesModel,
      selectedPermissionsModel,
      onRolesChange,
      onPermissionsChange,
    };
  },
});
